import React, { useEffect, useState } from "react";
import style from "./paymentStatusCallback.module.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { callApi } from "../../../../api/fetch";
import Loading from "../../../../Shared/loading/loading";
import { ACCENTCOLOR } from "../../../../Shared/colors";
import { PaymentStatus, normalizePaymentStatus } from "../../../../constants/orderTypes";
import { isSuccessfulPaymentStatus, matchPaymentByLinkId } from "../../../../utils/paymentGateway";
import { ReactComponent as SuccessIcon } from "../../../../assets/images/success_ico.svg";
import { ReactComponent as ErrorIcon } from "../../../../assets/images/error_state.svg";
import { ReactComponent as Processing } from "../../../../assets/images/processing.svg";
import { testIdProps } from "../../../../utils/testIdProps";

const PaymentStatusCallback = () => {
  const history = useNavigate();
  const location = useLocation();

  const [paymentData, setPaymentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const onGoBack = () => {
    history("/application/billing");
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const linkId = query.get("link_id");
    if (linkId) {
      fetchData(linkId);
    }
  }, [location]);

  const fetchData = async (linkId: string) => {
    try {
      const orgId = localStorage.getItem("organisation_id");
      if (!orgId) {
        setLoading(false);
        return;
      }

      const response = await callApi(`/organisations/${orgId}/payments`, {
        method: "GET",
      });
      const payments = Array.isArray(response)
        ? response
        : (response as { data?: unknown[] })?.data ?? [];
      const matched = matchPaymentByLinkId(payments, linkId);

      if (matched) {
        setPaymentData(matched);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
    return;
  };

  const renderComponents = () => {
    const status = normalizePaymentStatus(paymentData?.status);
    if (isSuccessfulPaymentStatus(status)) {
      return <SuccessTxn onGoBack={onGoBack} paymentData={paymentData} />;
    }

    if (
      status === PaymentStatus.ERROR ||
      status === PaymentStatus.CANCELED ||
      status === PaymentStatus.EXPIRED
    ) {
      return (
        <ErrorTxn
          onGoBack={onGoBack}
          paymentData={paymentData}
          message="Transaction failed"
          subMessage="Your transaction failed at the server end. Please try to make the payment again"
        />
      );
    }

    if (status == PaymentStatus.ACTIVE || paymentData?.generated_link_id) {
      return <ActiveTxn onGoBack={onGoBack} paymentData={paymentData} />;
    }

    return onGoBack();
  };

  return (
    <main
      style={{ background: "#F3F4F6" }}
      className={
        "container d-flex justify-content-center align-items-center vh-100"
      }
    >
      <div className={"text-center " + style.response_container}>
        {loading ? (
          <Loading loadingColor={ACCENTCOLOR} width="14px" height="14px" />
        ) : (
          renderComponents() || null
        )}
      </div>
    </main>
  );
};

export default PaymentStatusCallback;

const SuccessTxn = ({ onGoBack, paymentData }: any) => {
  return (
    <>
      <div>
        <SuccessIcon></SuccessIcon>
      </div>
      <div className={style.txn_title_text + " my-4 " + style.success}>
        Transaction Successful
      </div>
      <div className={style.txn_success_sub + " my-4"}>
        Your recharge of{" "}
        <span className="font-weight-bold">₹{paymentData?.amount}</span> was
        processed successfully
      </div>
      <div className={style.txn_id + " my-4"}>
        Transaction ID: {paymentData?.order_id}
      </div>
      <button
        {...testIdProps("billing-payment-go-back")}
        className={style.go_back}
        onClick={onGoBack}
      >
        GO BACK TO BILLING
      </button>
    </>
  );
};

const ErrorTxn = ({ onGoBack, message, subMessage }: any) => {
  return (
    <>
      <div>
        <ErrorIcon></ErrorIcon>
      </div>
      <div className={style.txn_title_text + " my-4 " + style.error}>
        {message}
      </div>
      <div className={style.txn_success_sub + " my-4"}>{subMessage}</div>
      <button
        {...testIdProps("billing-payment-go-back")}
        className={style.go_back}
        onClick={onGoBack}
      >
        GO BACK TO BILLING
      </button>
    </>
  );
};

const ActiveTxn = ({ onGoBack }: any) => {
  return (
    <>
      <div>
        <Processing></Processing>
      </div>
      <div className={style.txn_title_text + " my-4 " + style.processing}>
        Fetching your transaction status
      </div>
      <div className={style.processing_sub + " my-4"}>
        It usually takes 2-3 seconds to update the status. Kindly wait till we
        get an update
      </div>
      <button
        {...testIdProps("billing-payment-go-back")}
        className={style.go_back}
        onClick={onGoBack}
      >
        GO BACK TO BILLING
      </button>
    </>
  );
};
